import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import classes from './OrgSectionNav.module.css';

export type OrgSectionId = 'details' | 'address' | 'mailing';

export interface OrgSectionMeta {
  readonly id: OrgSectionId;
  readonly label: string;
  readonly description: string;
}

/** The panels, in the order they appear on the page. */
export const ORG_SECTIONS: readonly OrgSectionMeta[] = [
  {
    id: 'details',
    label: 'Organization details',
    description: 'Legal name, size, and industry',
  },
  {
    id: 'address',
    label: 'Business address',
    description: 'Phone and physical location',
  },
  {
    id: 'mailing',
    label: 'Mailing address',
    description: 'Where correspondence is sent',
  },
];

interface OrgSectionNavProps {
  readonly activeId: OrgSectionId;
  readonly missingBySection: Record<OrgSectionId, number>;
  readonly onSelect: (id: OrgSectionId) => void;
}

const completionText = (missing: number): string => {
  if (missing === 0) {
    return 'Complete';
  }

  const noun = missing === 1 ? 'field' : 'fields';
  return `${missing} required ${noun} left`;
};

function SectionIcon({ missing }: Readonly<{ missing: number }>) {
  if (missing === 0) {
    return (
      <CheckCircleOutlineIcon
        fontSize="small"
        color="success"
        className={classes.icon}
        aria-hidden="true"
      />
    );
  }

  return (
    <RadioButtonUncheckedIcon
      fontSize="small"
      color="warning"
      className={classes.icon}
      aria-hidden="true"
    />
  );
}

/**
 * The rail beside the panels: one row per section, showing whether its required
 * fields are on file, and scrolling the page to it when pressed.
 *
 * <p>The completion sentence is rendered hidden and referenced by
 * `aria-describedby` rather than drawn in the row -- the design carries that
 * state in the icon alone, but a screen reader needs the words.
 */
export const OrgSectionNav = ({
  activeId,
  missingBySection,
  onSelect,
}: OrgSectionNavProps) => {
  return (
    <nav aria-label="Organization profile sections" className={classes.nav}>
      {ORG_SECTIONS.map((section) => {
        const active = section.id === activeId;
        const missing = missingBySection[section.id];
        const rowClass = active
          ? `${classes.row} ${classes.rowActive}`
          : classes.row;

        return (
          <button
            key={section.id}
            type="button"
            onClick={() => onSelect(section.id)}
            aria-current={active ? 'true' : undefined}
            aria-describedby={`${section.id}-nav-status`}
            className={rowClass}
          >
            <SectionIcon missing={missing} />
            <span>
              <span className={classes.label}>{section.label}</span>
              <span className={classes.description}>{section.description}</span>
              <span id={`${section.id}-nav-status`} hidden>
                {completionText(missing)}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default OrgSectionNav;
